import React, { useState } from 'react';
import { Linkedin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LinkedInLoginButton({ 
  variant = 'default',
  size = 'default',
  className = '',
  referralCode,
  affiliateCode,
  onSuccess,
  onError 
}) {
  const [isLoading, setIsLoading] = useState(false);

  const handleLinkedInLogin = async () => {
    setIsLoading(true);

    try {
      // Store referral/affiliate codes before redirect so they persist through OAuth flow
      if (referralCode) {
        sessionStorage.setItem('referral_code', referralCode);
      }
      if (affiliateCode) {
        sessionStorage.setItem('affiliate_code', affiliateCode);
      }
      
      // Get authorization URL from backend
      const response = await fetch(`${API_URL}/api/auth/linkedin/authorize`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to initiate LinkedIn login');
      }

      const { authorization_url } = await response.json();
      
      // Redirect to LinkedIn authorization page
      window.location.href = authorization_url;
      
    } catch (error) {
      console.error('LinkedIn login error:', error);
      toast.error('Failed to connect with LinkedIn');
      onError?.(error);
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleLinkedInLogin}
      disabled={isLoading}
      variant={variant}
      size={size}
      className={`bg-[#0077B5] hover:bg-[#005885] text-white whitespace-nowrap min-w-fit px-4 ${className}`}
    >
      {isLoading ? (
        <>
          <Loader2 className="w-5 h-5 mr-2 animate-spin flex-shrink-0" />
          <span className="hidden sm:inline">Connecting...</span>
          <span className="sm:hidden">...</span>
        </>
      ) : (
        <>
          <Linkedin className="w-5 h-5 mr-2 flex-shrink-0" />
          <span className="hidden sm:inline">Sign in with LinkedIn</span>
          <span className="sm:hidden">LinkedIn</span>
        </>
      )}
    </Button>
  );
}

// Compact version for settings pages
export function LinkedInConnectButton({ isConnected, onConnect, onDisconnect }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/linkedin/authorize`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to initiate LinkedIn connection');
      }

      const { authorization_url } = await response.json();
      window.location.href = authorization_url;
    } catch (error) {
      console.error('LinkedIn connect error:', error);
      toast.error('Failed to connect LinkedIn');
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/linkedin/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect LinkedIn');
      }

      toast.success('LinkedIn disconnected');
      onDisconnect?.();
    } catch (error) {
      console.error('LinkedIn disconnect error:', error);
      toast.error('Failed to disconnect LinkedIn');
    } finally {
      setIsLoading(false);
    }
  };

  if (isConnected) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-green-600">
          <Linkedin className="w-5 h-5" />
          <span className="text-sm font-medium">Connected</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDisconnect}
          disabled={isLoading}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disconnect'}
        </Button>
      </div>
    );
  }

  return (
    <Button
      onClick={handleConnect}
      disabled={isLoading}
      variant="outline"
      size="sm"
      className="border-[#0077B5] text-[#0077B5] hover:bg-[#0077B5] hover:text-white"
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Linkedin className="w-4 h-4 mr-2" />
      )}
      Connect LinkedIn
    </Button>
  );
}
